import type { TenantResponse } from '@/api/responses'

import type {
  ChannelStatusValue,
  OnboardingSession,
  OnboardingStageValue,
  ProvisioningStatus,
  ProvisioningStepNameValue,
} from './types'

export type WizardStepId = 'organization' | 'playground' | 'connect' | 'goLive'

/**
 * Ordem do onboarding self-service (WP-SS-01, docs/addenda/.../01 secao 2): mostrar valor antes de
 * pedir toda a implantacao. O usuario cria a organizacao, EXPERIMENTA o agente no Playground e so
 * entao conecta o WhatsApp — que passa a ser uma acao explicita, nunca automatica.
 */
export const WIZARD_STEP_ORDER: readonly WizardStepId[] = [
  'organization',
  'playground',
  'connect',
  'goLive',
]

/**
 * O passo atual vem do backend, nunca so do browser: da `OnboardingSession` (estagio retomavel) e
 * de `tenant.status`. Fechar e reabrir refaz esta derivacao a partir do `GET /onboarding` +
 * `GET /tenants` mais recentes, entao a wizard sempre retoma no passo certo. `tenant.status` so
 * vira `Active` quando a saga de provisioning chega em `Ready` (`MarkReadyStep`) — isso vence e
 * leva direto ao passo final.
 */
export function resolveWizardStep(
  tenant: TenantResponse | undefined,
  session: OnboardingSession | undefined,
): WizardStepId {
  if (tenant === undefined) {
    return 'organization'
  }

  if (tenant.status === 'Active') {
    return 'goLive'
  }

  // Deriva o passo do estagio persistido da sessao (retomavel). A organizacao ja nasce com sessao
  // no estagio Playground (agente provisional criado junto). Sem sessao (organizacoes anteriores ao
  // recurso), cai no passo de conexao — o fluxo legado.
  const stage = session?.currentStage as OnboardingStageValue | undefined

  switch (stage) {
    case 'Organization':
    case 'ProvisionalAgent':
    case 'Playground':
      return 'playground'
    case 'Channel':
      return 'connect'
    case 'GoLive':
      return 'goLive'
    default:
      return session === undefined ? 'connect' : 'playground'
  }
}

export type MilestoneState = 'pending' | 'active' | 'done' | 'error'

/** Literal (nao `string`) para que `t(milestone.labelKey)` continue type-safe (react-i18next-typed). */
export type MilestoneLabelKey =
  | 'onboarding.connect.progress.milestones.channel'
  | 'onboarding.connect.progress.milestones.pairing'
  | 'onboarding.connect.progress.milestones.agent'
  | 'onboarding.connect.progress.milestones.finish'

export interface Milestone {
  readonly id: string
  readonly labelKey: MilestoneLabelKey
  readonly state: MilestoneState
}

interface MilestoneDefinition {
  readonly id: string
  readonly labelKey: MilestoneLabelKey
  readonly steps: readonly ProvisioningStepNameValue[]
}

/**
 * Agrupa os passos da saga (`ProvisioningStepName`) em marcos legiveis para a wizard, sem nunca
 * citar o provedor de canal (docs/design/00 secao 5). A ordem segue a maquina de estados
 * (`ProvisioningStage`, sequencial): cada marco so conclui quando todos os seus passos concluem.
 */
const MILESTONE_DEFINITIONS: readonly MilestoneDefinition[] = [
  {
    id: 'channel',
    labelKey: 'onboarding.connect.progress.milestones.channel',
    steps: ['CreateChannelInstance'],
  },
  {
    id: 'pairing',
    labelKey: 'onboarding.connect.progress.milestones.pairing',
    steps: ['BeginPairing', 'AwaitChannelConnection'],
  },
  {
    id: 'agent',
    labelKey: 'onboarding.connect.progress.milestones.agent',
    steps: ['CreateDefaultAgent', 'CreateKnowledgeBase'],
  },
  {
    id: 'finish',
    labelKey: 'onboarding.connect.progress.milestones.finish',
    steps: ['ConfigureChannelWebhook', 'MarkReady'],
  },
]

/**
 * Deriva o checklist legivel ("Configurando seu atendimento...") a partir dos passos brutos da
 * saga. Marcos antes do primeiro incompleto ficam `done`; o primeiro incompleto e o atual
 * (`active`, ou `error` se a saga falhou); o resto fica `pending`. Isso reflete a maquina de
 * estados real (`ProvisioningWorkflow.AdvanceTo` so avanca um marco por vez), sem inventar
 * progresso.
 */
export function buildMilestones(status: ProvisioningStatus | undefined): readonly Milestone[] {
  const stepStatusByName = new Map((status?.steps ?? []).map((step) => [step.step, step.status]))
  const workflowFailed = status?.status === 'Failed'
  let currentAssigned = false

  return MILESTONE_DEFINITIONS.map((definition) => {
    const allCompleted = definition.steps.every(
      (name) => stepStatusByName.get(name) === 'Completed',
    )

    if (allCompleted) {
      return { id: definition.id, labelKey: definition.labelKey, state: 'done' as const }
    }

    if (currentAssigned) {
      return { id: definition.id, labelKey: definition.labelKey, state: 'pending' as const }
    }

    currentAssigned = true
    return {
      id: definition.id,
      labelKey: definition.labelKey,
      state: workflowFailed ? ('error' as const) : ('active' as const),
    }
  })
}

export function isProvisioningNotStarted(status: ProvisioningStatus | undefined): boolean {
  return status === undefined || status.status === 'NotStarted'
}

export function isProvisioningFailed(status: ProvisioningStatus | undefined): boolean {
  return status?.status === 'Failed'
}

export function isProvisioningComplete(status: ProvisioningStatus | undefined): boolean {
  return status?.status === 'Completed'
}

/**
 * O canal WhatsApp do onboarding, resolvido a partir de `ProvisioningStatusResponse.channelId`
 * (campo adicionado apos o gap identificado no WP-08b: a resposta original nao expunha o
 * `channelId` do canal criado pela saga, e nao havia endpoint de listagem de canais — sem um id,
 * a wizard nao conseguia chamar `GET /tenants/{tenantId}/channels/{channelId}/pairing`, cujo
 * `channelId` e path param obrigatorio e um Guid gerado pelo servidor). Continua sendo o unico
 * ponto que le esse campo: se o formato mudar de novo, so este arquivo muda.
 */
export function deriveChannelId(status: ProvisioningStatus | undefined): string | undefined {
  return status?.channelId ?? undefined
}

export function deriveChannelStatus(
  status: ProvisioningStatus | undefined,
): ChannelStatusValue | undefined {
  return (status?.channelStatus as ChannelStatusValue | null | undefined) ?? undefined
}

/**
 * A tela de QR so faz sentido quando ha um canal para buscar E ele esta explicitamente aguardando
 * pareamento (docs/05 secao 8) — `channelStatus` e a fonte de verdade (mesma transicao que
 * `BeginPairingStep`/`Channel.MarkWaitingForPairing` no backend), mais precisa que o `stage` do
 * workflow para esta decisao especifica.
 */
export function shouldShowPairing(status: ProvisioningStatus | undefined): boolean {
  return (
    deriveChannelId(status) !== undefined && deriveChannelStatus(status) === 'WaitingForPairing'
  )
}

/**
 * O canal ja conectou (`Channel.MarkConnected`, mesma transicao que avanca o workflow para
 * `ChannelConnected`): a wizard deixa de mostrar o QR e volta a mostrar o checklist de progresso
 * enquanto o resto da saga (agente default, base de conhecimento, webhook de entrada) termina.
 */
export function isChannelConnected(status: ProvisioningStatus | undefined): boolean {
  return deriveChannelStatus(status) === 'Connected'
}

/**
 * A Api nao devolve TTL do QR (`ChannelPairingCode` so tem `ObtainedAt`). Estimativa
 * conservadora client-side, so para a UX de expiracao/refresh (docs/design/05 secao 4); "refresh"
 * de fato e apenas repetir o GET, que sempre devolve o QR atual do provider.
 */
export const QR_ASSUMED_TTL_SECONDS = 60

/** `qrCodeBase64` pode vir como base64 puro ou ja como data URI, dependendo do provider. */
export function toQrImageSrc(qrCodeBase64: string): string {
  return qrCodeBase64.startsWith('data:') ? qrCodeBase64 : `data:image/png;base64,${qrCodeBase64}`
}
